import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Settings as SettingsIcon, User as UserIcon, Bell, HelpCircle } from "lucide-react";
import { api, API_ORIGIN } from "../lib/api";
import { useAuth } from "../auth/AuthContext";
import { UpstoxAutoLoginPanel } from "../components/UpstoxAutoLoginPanel";
import { KiteAutoLoginPanel } from "../components/KiteAutoLoginPanel";
import { AccountPnL } from "../components/AccountPnL";

export const Settings = () => {
  const { user, refresh } = useAuth();
  const [me, setMe] = useState(null);
  const [upstoxForm, setUpstoxForm] = useState({
    api_key: "", api_secret: "", redirect_uri: "", totp_secret: "", mobile: "", pin: "",
  });
  // 2026-09-12 — Zerodha (Kite Connect) per-user credentials, mirrors
  // upstoxForm exactly (chat: "implement Zerodha API Credentials with TOTP
  // (similar to Upstox)"). Field names follow Kite's own login vocabulary —
  // see KiteConfig's docstring in models/user.py.
  const [kiteForm, setKiteForm] = useState({
    api_key: "", api_secret: "", redirect_uri: "", totp_secret: "", user_id: "", password: "",
  });
  const [telegramForm, setTelegramForm] = useState({ chat_id: "", bot_token: "" });
  // 2026-09-13 — chat: "User Profile: User should be able to check his
  // Profile and should be able to edit PAN and Phone number, City (not
  // email)." Reuses the SAME PUT /auth/profile endpoint ProfileSetup.jsx
  // calls at signup — it already whitelists only display_name/full_name/
  // phone/pan/city server-side (auth_service.auth_update_profile), so email
  // can't be changed through this even if someone tampered with the payload.
  // Only phone/pan/city are exposed as editable here; display name and
  // email are shown read-only (view-only, per the ask).
  const [profileForm, setProfileForm] = useState({
    phone: user?.profile?.phone || "",
    pan: user?.profile?.pan || "",
    city: user?.profile?.city || "",
  });
  const [profileSaving, setProfileSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/me/settings");
      setMe(data);
      // Phase 47c — pre-fill non-secret Upstox fields from server so the
      // user sees what's already on file (API Key, Redirect URI, Mobile).
      // Secrets stay empty; placeholders below show "Saved" when on file.
      const up = data?.upstox || {};
      setUpstoxForm((prev) => ({
        ...prev,
        api_key: up.api_key || "",
        redirect_uri: up.redirect_uri || "",
        mobile: up.mobile || "",
      }));
      // 2026-09-12 — same pre-fill pattern for Zerodha (non-secret fields only).
      const kt = data?.kite || {};
      setKiteForm((prev) => ({
        ...prev,
        api_key: kt.api_key || "",
        redirect_uri: kt.redirect_uri || "",
        user_id: kt.user_id || "",
      }));
    })();
  }, []);

  // Phase 47d — Detect the OAuth callback redirect (server-side) and show a
  // matching toast. ?upstox=connected → success · =error → failure ·
  // =missing_code → user cancelled the Upstox sign-in.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("upstox");
    if (!status) return;
    if (status === "connected") {
      toast.success("🔄 Upstox connected — daily access token refreshed.");
    } else if (status === "error") {
      toast.error("Upstox login failed — please try again.");
    } else if (status === "missing_code") {
      toast.error("Upstox login cancelled.");
    }
    // Strip the param so a page refresh doesn't re-fire the toast.
    const url = new URL(window.location.href);
    url.searchParams.delete("upstox");
    window.history.replaceState({}, "", url.pathname + url.search);
  }, []);

  // 2026-09-12 — same OAuth-callback-toast detection for Zerodha
  // (?kite=connected|error|missing_token, set by /api/kite/callback).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("kite");
    if (!status) return;
    if (status === "connected") {
      toast.success("🔄 Zerodha connected — access token saved.");
    } else if (status === "error") {
      toast.error("Zerodha login failed — please try again.");
    } else if (status === "missing_token") {
      toast.error("Zerodha login cancelled.");
    }
    const url = new URL(window.location.href);
    url.searchParams.delete("kite");
    window.history.replaceState({}, "", url.pathname + url.search);
  }, []);

  const updateSetting = async (k, v) => {
    try {
      await api.put("/me/settings", { [k]: v });
      toast.success(`${k} ${v ? "enabled" : "disabled"}`);
      const { data } = await api.get("/me/settings");
      setMe(data);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const saveProfile = async () => {
    if (!profileForm.phone || !profileForm.pan || !profileForm.city) {
      toast.error("Phone, PAN and City are all required.");
      return;
    }
    setProfileSaving(true);
    try {
      await api.put("/auth/profile", {
        phone: profileForm.phone,
        pan: profileForm.pan,
        city: profileForm.city,
      });
      await refresh();
      toast.success("Profile updated.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update profile");
    } finally {
      setProfileSaving(false);
    }
  };

  const saveUpstox = async () => {
    const payload = Object.fromEntries(Object.entries(upstoxForm).filter(([_, v]) => v));
    if (!Object.keys(payload).length) {
      toast.error("Enter at least one Upstox field");
      return;
    }
    await api.put("/me/upstox", payload);
    toast.success("Upstox credentials saved (encrypted at rest)");
    // Phase 47c — clear ONLY the secret fields after save; keep API Key /
    // Redirect URI / Mobile visible so the user can verify what's on file.
    setUpstoxForm((prev) => ({
      ...prev,
      api_secret: "", totp_secret: "", pin: "",
    }));
    const { data } = await api.get("/me/settings");
    setMe(data);
    const up = data?.upstox || {};
    setUpstoxForm((prev) => ({
      ...prev,
      api_key: up.api_key || "",
      redirect_uri: up.redirect_uri || "",
      mobile: up.mobile || "",
    }));
  };

  const saveKite = async () => {
    const payload = Object.fromEntries(Object.entries(kiteForm).filter(([_, v]) => v));
    if (!Object.keys(payload).length) {
      toast.error("Enter at least one Zerodha field");
      return;
    }
    await api.put("/me/kite", payload);
    toast.success("Zerodha credentials saved (encrypted at rest)");
    // Clear ONLY the secret fields after save; keep API Key / Redirect URI /
    // User ID visible so the user can verify what's on file — same as Upstox.
    setKiteForm((prev) => ({
      ...prev,
      api_secret: "", totp_secret: "", password: "",
    }));
    const { data } = await api.get("/me/settings");
    setMe(data);
    const kt = data?.kite || {};
    setKiteForm((prev) => ({
      ...prev,
      api_key: kt.api_key || "",
      redirect_uri: kt.redirect_uri || "",
      user_id: kt.user_id || "",
    }));
  };

  const saveTelegram = async () => {
    const payload = Object.fromEntries(
      Object.entries(telegramForm).filter(([_, v]) => v),
    );
    if (!Object.keys(payload).length) {
      toast.error("Enter your Telegram chat ID (and optionally a bot token)");
      return;
    }
    try {
      await api.put("/me/telegram", payload);
      toast.success("Telegram alerts routed to your chat");
      setTelegramForm({ chat_id: "", bot_token: "" });
      const { data } = await api.get("/me/settings");
      setMe(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save Telegram");
    }
  };

  const [tgTesting, setTgTesting] = useState(false);
  const sendTestTelegram = async () => {
    setTgTesting(true);
    try {
      const { data } = await api.post("/me/telegram/test");
      if (data?.ok) {
        toast.success("Test message sent — check your Telegram");
      } else {
        toast.error(`Telegram test failed: ${data?.error || "unknown error"}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Telegram test failed");
    } finally {
      setTgTesting(false);
    }
  };

  const [upTesting, setUpTesting] = useState(false);
  const testUpstox = async () => {
    setUpTesting(true);
    try {
      const { data } = await api.post("/me/upstox/test");
      if (data?.ok) {
        if (data?.refreshed) {
          toast.success(
            `🔄 Auto-refreshed daily token · Nifty 50 ₹${data.nifty_ltp}`,
          );
        } else {
          toast.success(
            `Upstox OK · Nifty 50 ₹${data.nifty_ltp}${data.user_name ? ` · ${data.user_name}` : ""}`,
          );
        }
      } else {
        toast.error(`Upstox test failed: ${data?.error || "unknown error"}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upstox test failed");
    } finally {
      setUpTesting(false);
    }
  };

  const [kiTesting, setKiTesting] = useState(false);
  const testKite = async () => {
    setKiTesting(true);
    try {
      const { data } = await api.post("/me/kite/test");
      if (data?.ok) {
        toast.success(`Zerodha OK${data.user_name ? ` · ${data.user_name}` : ""}`);
      } else {
        toast.error(`Zerodha test failed: ${data?.error || "unknown error"}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Zerodha test failed");
    } finally {
      setKiTesting(false);
    }
  };

  const [loginBusy, setLoginBusy] = useState(false);
  const loginWithUpstox = async () => {
    setLoginBusy(true);
    try {
      const { data } = await api.get("/upstox/auth-url");
      if (data?.auth_url) {
        // Send the user to Upstox to authenticate. The callback at
        // /api/upstox/callback persists the token and bounces back to /?upstox=connected
        window.location.href = data.auth_url;
      } else {
        toast.error("Upstox login URL not available");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to start Upstox login");
    } finally {
      setLoginBusy(false);
    }
  };

  const loginWithKite = () => {
    // /api/kite/login is a direct RedirectResponse (unlike Upstox's JSON-
    // wrapping /upstox/auth-url) — just navigate the browser there and let
    // Kite's own hosted login page (and eventually /api/kite/callback)
    // handle the rest.
    window.location.href = `${API_ORIGIN}/api/kite/login`;
  };

  if (!me) return null;

  const s = me.settings || {};
  const liveLocked = !user?.live_trading_enabled;
  const commodityLiveLocked = !user?.commodity_live_trading_enabled;
  const isAdminUser = user?.role === "ADMIN";

  return (
    <section className="space-y-6" data-testid="user-settings-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data mb-1">
          Account · Settings
        </div>
        <h2 className="font-heading text-2xl flex items-center gap-2"><SettingsIcon size={20} /> {isAdminUser ? "Trading & Upstox" : "Trading & Zerodha"}</h2>
      </div>

      {/* My Profile — 2026-09-13: view profile, edit Phone/PAN/City only
          (email and display/full name are read-only here). */}
      <div className="border border-grid p-5 space-y-4" data-testid="my-profile-section">
        <h3 className="text-xs uppercase tracking-[0.2em] font-mono-data text-muted-2 flex items-center gap-2">
          <UserIcon size={12} /> My Profile
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">Email</label>
            <div
              className="w-full border border-grid/60 px-3 py-2 text-sm text-muted-2"
              style={{ background: "rgba(255,255,255,0.02)" }}
              data-testid="profile-email-readonly"
              title="Email can't be changed here."
            >
              {user?.email}
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">Display Name</label>
            <div
              className="w-full border border-grid/60 px-3 py-2 text-sm text-muted-2"
              style={{ background: "rgba(255,255,255,0.02)" }}
              data-testid="profile-display-name-readonly"
            >
              {user?.profile?.display_name || user?.name || "—"}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">Phone</label>
            <input
              type="text"
              value={profileForm.phone}
              onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
              placeholder="+91 90000 00000"
              data-testid="profile-phone-input"
              className="w-full bg-transparent border border-grid px-3 py-2 text-sm focus:outline-none focus:border-white/40"
            />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">PAN</label>
            <input
              type="text"
              value={profileForm.pan}
              onChange={(e) => setProfileForm({ ...profileForm, pan: e.target.value.toUpperCase() })}
              placeholder="ABCDE1234F"
              data-testid="profile-pan-input"
              className="w-full bg-transparent border border-grid px-3 py-2 text-sm focus:outline-none focus:border-white/40"
            />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">City</label>
            <input
              type="text"
              value={profileForm.city}
              onChange={(e) => setProfileForm({ ...profileForm, city: e.target.value })}
              placeholder="Bengaluru"
              data-testid="profile-city-input"
              className="w-full bg-transparent border border-grid px-3 py-2 text-sm focus:outline-none focus:border-white/40"
            />
          </div>
        </div>
        <button
          onClick={saveProfile}
          disabled={profileSaving}
          data-testid="profile-save-btn"
          className="px-4 py-2 border text-[11px] uppercase tracking-[0.2em] font-mono-data disabled:opacity-50"
          style={{ color: "var(--bull)", borderColor: "var(--bull)" }}
        >
          {profileSaving ? "Saving..." : "Save Profile"}
        </button>
      </div>

      {/* Trading toggles */}
      <div className="border border-grid p-5 space-y-4">
        <h3 className="text-xs uppercase tracking-[0.2em] font-mono-data text-muted-2 flex items-center gap-2">
          <Bell size={12} /> Trading Mode
        </h3>
        <Toggle label="Paper Trading"
                hint="All signals simulated. Safe to leave on permanently."
                checked={s.paper_trading}
                onChange={(v) => updateSetting("paper_trading", v)}
                testid="setting-paper-trading" />
        <Toggle label="Live Trading"
                hint={liveLocked
                  ? "Locked — an admin must enable Live Trading on your account first."
                  : "Real orders in your Zerodha account, fired by the auto-pilot. Use with full conviction only."}
                checked={s.live_trading}
                disabled={liveLocked && !s.live_trading}
                onChange={(v) => updateSetting("live_trading", v)}
                testid="setting-live-trading" />
        <Toggle label="Telegram Alerts"
                hint="Per-section pause toggles still apply (Section 5/6/7/8)."
                checked={s.telegram_alerts}
                onChange={(v) => updateSetting("telegram_alerts", v)}
                testid="setting-telegram-alerts" />
      </div>

      {/* Commodity (MCX) trading toggles */}
      <div className="border border-grid p-5 space-y-4">
        <h3 className="text-xs uppercase tracking-[0.2em] font-mono-data text-muted-2 flex items-center gap-2">
          <Bell size={12} /> Commodity Trading Mode
        </h3>
        <Toggle label="Commodity Paper Trading"
                hint="MCX signals simulated (Black-76 estimate or real quote). Safe to leave on permanently."
                checked={s.commodity_paper_trading}
                onChange={(v) => updateSetting("commodity_paper_trading", v)}
                testid="setting-commodity-paper-trading" />
        <Toggle label="Commodity Live Trading"
                hint={commodityLiveLocked && s.commodity_live_trading
                  ? "Still ON although the admin grant is off — untick to stop real MCX orders. (Turning ON again needs the grant.)"
                  : commodityLiveLocked
                  ? "Locked — an admin must enable Commodity Live Trading on your account first."
                  : me.kite_is_primary
                    ? "MASTER SWITCH for real MCX orders placed by the autopilot (all live commodity strategies). OFF = signals stay paper. Replaces the old Kite Execution card on Commodities → Live Orders."
                    : "Real MCX orders in your Zerodha account. Only ever fires off a real quote, never an estimate."}
                checked={s.commodity_live_trading}
                disabled={commodityLiveLocked && !s.commodity_live_trading}
                onChange={(v) => updateSetting("commodity_live_trading", v)}
                testid="setting-commodity-live-trading" />
      </div>

      {/* Telegram per-user routing */}
      <div className="border border-grid p-5 space-y-4">
        <h3 className="text-xs uppercase tracking-[0.2em] font-mono-data text-muted-2 flex items-center gap-2">
          <Bell size={12} /> Personal Telegram
        </h3>
        <p className="text-[11px] text-muted-2 leading-relaxed">
          Connect your personal Telegram chat to receive all auto-pilot alerts
          directly. We&apos;ll send to your chat in <i>addition</i> to the global
          channel — flip <span className="text-foreground">Telegram Alerts</span>{" "}
          above to ON to receive them.
          {me.telegram_configured && (
            <span className="ml-2" style={{ color: "var(--bull)" }}>
              · Connected (ends …{me.telegram_chat_id_last4}).
            </span>
          )}
        </p>
        <ol className="text-[11px] text-muted-2 leading-relaxed list-decimal pl-4 space-y-1">
          <li>Open Telegram, message <code>@userinfobot</code> → copy your <b>chat ID</b>.</li>
          <li>(Optional) Provide your own bot token (<code>BotFather</code>) for a private feed; otherwise we use the platform bot.</li>
          <li>Save below.</li>
        </ol>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">Telegram Chat ID</label>
            <input
              type="text"
              placeholder="e.g. 123456789"
              value={telegramForm.chat_id}
              onChange={(e) => setTelegramForm({ ...telegramForm, chat_id: e.target.value })}
              data-testid="telegram-chat-id"
              className="w-full bg-transparent border border-grid px-3 py-2 text-sm font-mono-data"
            />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">Bot Token <span className="normal-case text-muted-3">(optional)</span></label>
            <input
              type="password"
              placeholder="123456:ABC-DEF..."
              value={telegramForm.bot_token}
              onChange={(e) => setTelegramForm({ ...telegramForm, bot_token: e.target.value })}
              data-testid="telegram-bot-token"
              className="w-full bg-transparent border border-grid px-3 py-2 text-sm font-mono-data"
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={saveTelegram}
            data-testid="telegram-save"
            className="px-4 py-2 border text-[11px] uppercase tracking-[0.2em] font-mono-data"
            style={{ color: "var(--bull)", borderColor: "var(--bull)" }}
          >
            Save Telegram
          </button>
          <button
            onClick={sendTestTelegram}
            disabled={tgTesting || !me.telegram_configured}
            data-testid="telegram-test"
            className="px-4 py-2 border text-[11px] uppercase tracking-[0.2em] font-mono-data disabled:opacity-40"
            style={{ borderColor: "var(--grid)", color: "var(--text-primary)" }}
            title={me.telegram_configured ? "Send a test message to your saved chat" : "Save your chat ID first"}
          >
            {tgTesting ? "Sending..." : "Send Test Message"}
          </button>
        </div>
      </div>

      {/* Upstox credentials — 2026-10-15: admin only (chat: "For my wife's account -
          Settings page, Upstox is visible...can we hide it? that's not needed").
          Since the 1 Oct Kite cutover every live order goes through Zerodha;
          Upstox is only the platform's own market-data connection. */}
      {isAdminUser && (
      <div className="border border-grid p-5 space-y-4">
        <h3 className="text-xs uppercase tracking-[0.2em] font-mono-data text-muted-2 flex items-center gap-2">
          <UserIcon size={12} /> Upstox API Credentials
        </h3>
        <p className="text-[11px] text-muted-2 leading-relaxed">
          Stored encrypted at rest. Phase 1 records these for auditing — live order
          routing currently runs through the admin's global Upstox token. Per-user
          order routing is a follow-on task.
          {me.upstox_configured && (
            <span className="ml-2" style={{ color: "var(--bull)" }}>
              · Credentials saved on file.
            </span>
          )}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            ["api_key", "API Key", "Visible", "From upstox.com/developer/apps"],
            ["api_secret", "API Secret", "Secret", "Hidden after save"],
            ["redirect_uri", "Callback / Redirect URL", "Visible",
             "e.g. https://stoxtrade.in/api/upstox/callback"],
            ["totp_secret", "TOTP Secret", "Secret",
             "Base32 string from your Upstox 2FA setup"],
            ["mobile", "Mobile", "Visible", "10-digit registered mobile (no +91)"],
            ["pin", "Upstox Login PIN", "Secret",
             "The 6-digit PIN you enter on the Upstox mobile app"],
          ].map(([k, label, sensitivity, hint]) => {
            const isSecret = sensitivity === "Secret";
            const up = me?.upstox || {};
            const savedFlag =
              (k === "api_secret" && up.api_secret_configured) ||
              (k === "totp_secret" && up.totp_secret_configured) ||
              (k === "pin" && up.pin_configured);
            return (
              <div key={k} className="space-y-1">
                <div className="flex items-baseline justify-between">
                  <label className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">
                    {label}
                  </label>
                  {isSecret && savedFlag && (
                    <span
                      data-testid={`upstox-${k.replace("_", "-")}-saved`}
                      className="text-[9px] uppercase tracking-[0.2em] font-mono-data"
                      style={{ color: "var(--bull)" }}
                    >
                      ✓ saved
                    </span>
                  )}
                </div>
                <input
                  type={isSecret ? "password" : "text"}
                  value={upstoxForm[k]}
                  onChange={(e) => setUpstoxForm({ ...upstoxForm, [k]: e.target.value })}
                  data-testid={`upstox-${k.replace("_", "-")}`}
                  placeholder={isSecret && savedFlag ? "•••••••• (saved — type to replace)" : ""}
                  className="w-full bg-transparent border border-grid px-3 py-2 text-sm font-mono-data"
                />
                <div className="text-[9px] text-muted-3 font-mono-data">{hint}</div>
              </div>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={saveUpstox}
            data-testid="upstox-save"
            className="px-4 py-2 border text-[11px] uppercase tracking-[0.2em] font-mono-data"
            style={{ color: "var(--bull)", borderColor: "var(--bull)" }}
          >
            Save Upstox Credentials
          </button>
          <button
            onClick={testUpstox}
            disabled={upTesting}
            data-testid="upstox-test"
            className="px-4 py-2 border text-[11px] uppercase tracking-[0.2em] font-mono-data disabled:opacity-40"
            style={{ borderColor: "var(--grid)", color: "var(--text-primary)" }}
            title="Probe the platform's Upstox connection (admin's broker)"
          >
            {upTesting ? "Testing..." : "Test Connection"}
          </button>
          {(user?.role === "ADMIN") && (
            <button
              onClick={loginWithUpstox}
              disabled={loginBusy}
              data-testid="upstox-oauth-login"
              className="px-4 py-2 border text-[11px] uppercase tracking-[0.2em] font-mono-data disabled:opacity-40 inline-flex items-center gap-2"
              style={{ borderColor: "#7c4dff", color: "#b39dff" }}
              title="Opens Upstox login in this tab. Sign in to refresh the daily access token without using TOTP."
            >
              {loginBusy ? "Redirecting..." : "Login with Upstox →"}
            </button>
          )}
        </div>
        {(user?.role === "ADMIN") && (
          <p className="text-[10px] text-muted-3 font-mono-data leading-relaxed mt-1">
            <strong>Tip:</strong> Use <span style={{ color: "#b39dff" }}>Login with Upstox</span>{" "}
            when TOTP auto-login fails. It opens the official Upstox sign-in
            page; after authorisation you&apos;ll be redirected back here with a
            fresh daily token persisted.
          </p>
        )}
      </div>
      )}

      {/* Zerodha (Kite Connect) credentials — 2026-09-12, mirrors the
          Upstox API Credentials block above exactly (chat: "implement
          Zerodha API Credentials with TOTP (similar to Upstox)"). Same
          Phase-1 caveat: stored per-user, but live order routing still
          goes through kite_service.py's single global (admin) token. */}
      <div className="border border-grid p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="text-xs uppercase tracking-[0.2em] font-mono-data text-muted-2 flex items-center gap-2">
            <UserIcon size={12} /> Zerodha API Credentials
          </h3>
          <Link
            to="/settings/zerodha-setup-guide"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="zerodha-setup-guide-link"
            className="text-[10px] uppercase tracking-[0.2em] font-mono-data inline-flex items-center gap-1.5 underline underline-offset-2"
            style={{ color: "var(--bull)" }}
          >
            <HelpCircle size={12} /> How do I get these?
          </Link>
        </div>
        <p className="text-[11px] text-muted-2 leading-relaxed">
          Stored encrypted at rest.{" "}
          {me.kite_is_primary
            ? "Your account is the platform's primary Zerodha connection."
            : <>Used for your own live Commodities orders on <b>Commodities → Live Orders</b> once the admin
              enables your account
              {me.kite_link ? (me.kite_link.enabled
                ? <span style={{ color: "var(--bull)" }}> · enabled{me.kite_link.connected ? " · logged in today" : ""}</span>
                : <span className="text-muted-3"> · awaiting admin approval</span>) : null}.
              In your Kite app set the Redirect URL to the value below and add the platform&apos;s static IP.</>}
          {me.kite_configured && (
            <span className="ml-2" style={{ color: "var(--bull)" }}>
              · Credentials saved on file.
            </span>
          )}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            ["api_key", "API Key", "Visible", "From developers.kite.trade → My Apps"],
            ["api_secret", "API Secret", "Secret", "Hidden after save"],
            ["redirect_uri", "Redirect URL", "Visible",
             me.kite_redirect_url ? `Set your Kite app's Redirect URL to: ${me.kite_redirect_url}` : "e.g. https://stoxtrade.tech/api/kite/callback"],
            ["totp_secret", "TOTP Secret", "Secret",
             "The secret key shown when you enable External TOTP on Zerodha"],
            ["user_id", "Zerodha Client / User ID", "Visible", "e.g. AB1234"],
            ["password", "Zerodha Password", "Secret", "Your Kite login password"],
          ].map(([k, label, sensitivity, hint]) => {
            const isSecret = sensitivity === "Secret";
            const kt = me?.kite || {};
            const savedFlag =
              (k === "api_secret" && kt.api_secret_configured) ||
              (k === "totp_secret" && kt.totp_secret_configured) ||
              (k === "password" && kt.password_configured);
            return (
              <div key={k} className="space-y-1">
                <div className="flex items-baseline justify-between">
                  <label className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">
                    {label}
                  </label>
                  {isSecret && savedFlag && (
                    <span
                      data-testid={`kite-${k.replace("_", "-")}-saved`}
                      className="text-[9px] uppercase tracking-[0.2em] font-mono-data"
                      style={{ color: "var(--bull)" }}
                    >
                      ✓ saved
                    </span>
                  )}
                </div>
                <input
                  type={isSecret ? "password" : "text"}
                  value={kiteForm[k]}
                  onChange={(e) => setKiteForm({ ...kiteForm, [k]: e.target.value })}
                  data-testid={`kite-${k.replace("_", "-")}`}
                  placeholder={isSecret && savedFlag ? "•••••••• (saved — type to replace)" : ""}
                  className="w-full bg-transparent border border-grid px-3 py-2 text-sm font-mono-data"
                />
                <div className="text-[9px] text-muted-3 font-mono-data">{hint}</div>
              </div>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={saveKite}
            data-testid="kite-save"
            className="px-4 py-2 border text-[11px] uppercase tracking-[0.2em] font-mono-data"
            style={{ color: "var(--bull)", borderColor: "var(--bull)" }}
          >
            Save Zerodha Credentials
          </button>
          <button
            onClick={testKite}
            disabled={kiTesting}
            data-testid="kite-test"
            className="px-4 py-2 border text-[11px] uppercase tracking-[0.2em] font-mono-data disabled:opacity-40"
            style={{ borderColor: "var(--grid)", color: "var(--text-primary)" }}
            title={me.kite_is_primary ? "Probe the platform's Zerodha connection" : "Probe your own Zerodha login"}
          >
            {kiTesting ? "Testing..." : "Test Connection"}
          </button>
          {(!me.kite_is_primary && me.kite_link?.enabled) && (
            <button
              onClick={() => { window.location.href = `${API_ORIGIN}/api/kite-accounts/${user?.user_id}/login`; }}
              data-testid="kite-linked-login"
              className="px-4 py-2 border text-[11px] uppercase tracking-[0.2em] font-mono-data inline-flex items-center gap-2"
              style={{ borderColor: "#7c4dff", color: "#b39dff" }}
              title="Log in to your own Zerodha account for today"
            >
              Login with Zerodha →
            </button>
          )}
          {(user?.role === "ADMIN") && (
            <button
              onClick={loginWithKite}
              data-testid="kite-oauth-login"
              className="px-4 py-2 border text-[11px] uppercase tracking-[0.2em] font-mono-data inline-flex items-center gap-2"
              style={{ borderColor: "#7c4dff", color: "#b39dff" }}
              title="Opens Zerodha login in this tab. Sign in to refresh the daily access token."
            >
              Login with Zerodha →
            </button>
          )}
        </div>
        {(user?.role === "ADMIN") && (
          <p className="text-[10px] text-muted-3 font-mono-data leading-relaxed mt-1">
            <strong>Tip:</strong> Use <span style={{ color: "#b39dff" }}>Login with Zerodha</span>{" "}
            to refresh the daily access token. It opens the official Kite sign-in
            page; after authorisation you&apos;ll be redirected back here with a
            fresh token persisted. Not tech-savvy? See{" "}
            <Link to="/settings/zerodha-setup-guide" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2" style={{ color: "#b39dff" }}>
              the step-by-step guide
            </Link>.
          </p>
        )}
      </div>

      {/* 2026-08-25 — this is the panel that actually drives the 03:35 IST
          scheduled token refresh (upstox_autologin.py). It used to be
          rendered with hidden={true} inside the Candle Breakout page, so
          it was never reachable and never got configured — the credential
          fields above ("Upstox API Credentials") save to a separate,
          currently-unused per-user store and were never wired to this job.
          Configuring THIS panel is what stops the daily manual "Login with
          Upstox" click from being necessary. Gated to admins, same as the
          manual login button above, since it controls the one shared
          platform-wide broker connection. */}
      {(user?.role === "ADMIN") && (
        <UpstoxAutoLoginPanel />
      )}

      {/* 2026-09-20 — Kite (Zerodha) equivalent of the panel above (chat:
          "Need to implement TOTP for Zerodha"). Same relationship: the
          "Zerodha API Credentials" fields above save to a separate,
          Phase-1 audit-only per-user store and are NOT wired to any login
          flow — THIS panel drives the actual 08:30 IST scheduled token
          refresh (kite_autologin.py). Gated to admins for the same reason
          as Upstox's — one shared platform-wide broker connection. */}
      {(user?.role === "ADMIN") && (
        <KiteAutoLoginPanel />
      )}
    </section>
  );
};

const Toggle = ({ label, hint, checked, disabled, onChange, testid }) => (
  <div className="flex items-start justify-between gap-4 border border-grid p-3">
    <div className="space-y-1">
      <div className="text-sm">{label}</div>
      <div className="text-[10px] text-muted-3 font-mono-data leading-snug">{hint}</div>
    </div>
    <label className="inline-flex items-center gap-2 shrink-0">
      <input
        type="checkbox"
        checked={!!checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testid}
      />
      <span className="text-[10px] uppercase tracking-wider font-mono-data"
            style={{ color: checked ? "var(--bull)" : "var(--muted-3)" }}>
        {checked ? "ON" : "OFF"}
      </span>
    </label>
  </div>
);


export const Reports = () => {
  const [data, setData] = useState(null);
  // 2026-08-26 — From/To range filter (chat: "Better to add filters like
  // From and To Selection (useful in future)"). Additive: fetching a
  // custom range doesn't remove the always-present weekly/monthly cards,
  // it just adds a third one when both dates are set.
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [customLoading, setCustomLoading] = useState(false);

  const load = async (params) => {
    try {
      const { data } = await api.get("/me/reports", { params });
      setData(data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { load(); }, []);

  const applyRange = async () => {
    if (!fromDate || !toDate) {
      toast.error("Pick both From and To dates");
      return;
    }
    setCustomLoading(true);
    try {
      await load({ from_date: fromDate, to_date: toDate });
    } finally {
      setCustomLoading(false);
    }
  };

  const clearRange = () => {
    setFromDate("");
    setToDate("");
    load();
  };

  if (!data) return null;

  const fmtRs = (v) =>
    v == null || isNaN(Number(v))
      ? "—"
      : `${v >= 0 ? "+" : ""}₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
  const fmtPct = (v) =>
    v == null || isNaN(Number(v))
      ? "—"
      : `${v >= 0 ? "+" : ""}${Number(v).toFixed(2)}%`;

  const colorFor = (v) => (v > 0 ? "bull" : v < 0 ? "bear" : null);

  const Card = ({ title, rollup, testid }) => {
    if (!rollup) return null;
    const rows = [
      ["Trades", rollup.trades],
      ["Wins", rollup.wins, "bull"],
      ["Losses", rollup.losses, "bear"],
      ["Win rate", `${rollup.win_rate_pct}%`],
      ["Gross P&L (₹)", fmtRs(rollup.gross_pnl_rs), colorFor(rollup.gross_pnl_rs)],
      ["Gross P&L (%)", fmtPct(rollup.gross_pnl_pct), colorFor(rollup.gross_pnl_pct)],
      // 2026-08-26 — net_pnl_rs/charges_rs were already computed by the
      // backend (Phase 60c) but never rendered here (chat: "Need to
      // include Gross and Net P&L in reports page including brokerage").
      ["Net P&L (₹)", fmtRs(rollup.net_pnl_rs), colorFor(rollup.net_pnl_rs)],
      ["Charges (₹)", rollup.charges_rs != null ? `−₹${Number(rollup.charges_rs).toLocaleString("en-IN", { maximumFractionDigits: 2 })}` : "—"],
    ];
    if (rollup.total_lots)
      rows.push(["Lots traded", rollup.total_lots]);
    if (rollup.best_trade)
      rows.push([`Best · ${rollup.best_trade.symbol}`, fmtPct(rollup.best_trade.pnl_pct), "bull"]);
    if (rollup.worst_trade && rollup.worst_trade.pnl_pct < 0)
      rows.push([`Worst · ${rollup.worst_trade.symbol}`, fmtPct(rollup.worst_trade.pnl_pct), "bear"]);
    return (
      <div className="border border-grid p-4 space-y-2" data-testid={testid}>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">{title}</div>
        {rows.map(([label, value, color]) => (
          <div key={label} className="flex justify-between text-sm font-mono-data">
            <span className="text-muted-2">{label}</span>
            <span style={color ? { color: `var(--${color})` } : {}}>{value}</span>
          </div>
        ))}
        {rollup.by_setup && Object.keys(rollup.by_setup).length > 0 && (
          <div className="pt-2 border-t border-grid mt-2">
            <div className="text-[10px] uppercase tracking-wider text-muted-3 font-mono-data mb-1">By Setup</div>
            {Object.entries(rollup.by_setup).map(([tag, b]) => (
              <div key={tag} className="flex justify-between text-[11px] font-mono-data">
                <span className="text-muted-2">{tag}</span>
                <span style={{ color: b.pnl_rs > 0 ? "var(--bull)" : b.pnl_rs < 0 ? "var(--bear)" : "var(--text-muted)" }}>
                  {b.trades}t · {fmtRs(b.pnl_rs)}
                </span>
              </div>
            ))}
          </div>
        )}
        {rollup.top_symbols && rollup.top_symbols.length > 0 && (
          <div className="pt-2 border-t border-grid mt-2">
            <div className="text-[10px] uppercase tracking-wider text-muted-3 font-mono-data mb-1">Top Symbols</div>
            {rollup.top_symbols.slice(0, 5).map((s) => (
              <div key={s.symbol} className="flex justify-between text-[11px] font-mono-data">
                <span className="text-muted-2">{s.symbol}</span>
                <span style={{ color: s.pnl_rs > 0 ? "var(--bull)" : s.pnl_rs < 0 ? "var(--bear)" : "var(--text-muted)" }}>
                  {s.trades}t · {fmtRs(s.pnl_rs)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <section className="space-y-6" data-testid="user-reports-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data mb-1">
          Account · Profit / Loss · LIVE only
        </div>
        <h2 className="font-heading text-2xl">Weekly &amp; Monthly</h2>
        <div className="text-[10px] text-muted-3 font-mono-data mt-1">
          Week starts {data.week_start_ist} · Month starts {data.month_start_ist} · Paper trades excluded
        </div>
      </div>
      {/* 2026-10-27 — real Kite fills per account, incl. manual orders (kite_ledger.py) */}
      <AccountPnL market="stocks" title="Stocks · account P&L from real fills (auto + manual)" />
      <h3 className="text-xs uppercase tracking-[0.2em] text-muted-2 font-mono-data">Autopilot trades (since Aug)</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-3">
          <h3 className="text-xs uppercase tracking-[0.2em] text-muted-2 font-mono-data">This Week · Live</h3>
          <Card title="Live Orders" rollup={data.weekly} testid="report-weekly-live" />
        </div>
        <div className="space-y-3">
          <h3 className="text-xs uppercase tracking-[0.2em] text-muted-2 font-mono-data">This Month · Live</h3>
          <Card title="Live Orders" rollup={data.monthly} testid="report-monthly-live" />
        </div>
      </div>

      {/* 2026-08-26 — custom From/To range (chat: "Better to add filters
          like From and To Selection (useful in future)"). Additive to the
          fixed weekly/monthly cards above, not a replacement. */}
      <div className="border-t border-grid pt-6 space-y-3">
        <h3 className="text-xs uppercase tracking-[0.2em] text-muted-2 font-mono-data">Custom Range · Live</h3>
        <div className="flex flex-wrap items-end gap-3">
          <label className="block">
            <div className="text-[10px] uppercase tracking-wider text-muted-3 mb-1 font-mono-data">From</div>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="border-grid surface px-2 py-1.5 text-xs font-mono-data"
              data-testid="report-range-from"
            />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-wider text-muted-3 mb-1 font-mono-data">To</div>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="border-grid surface px-2 py-1.5 text-xs font-mono-data"
              data-testid="report-range-to"
            />
          </label>
          <button
            onClick={applyRange}
            disabled={customLoading}
            className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-mono-data border border-grid hover:bg-white/5 disabled:opacity-50"
            data-testid="report-range-apply"
          >
            {customLoading ? "Loading…" : "Apply"}
          </button>
          {data.custom && (
            <button
              onClick={clearRange}
              className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-mono-data border border-grid hover:bg-white/5"
            >
              Clear
            </button>
          )}
        </div>
        {data.custom && (
          <div className="max-w-md">
            <Card
              title={`Live Orders · ${data.custom_from} → ${data.custom_to}`}
              rollup={data.custom}
              testid="report-custom-live"
            />
          </div>
        )}
      </div>
    </section>
  );
};
